import React, { FC, useState, useRef, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { authAxios } from "../utils/session_utils";
import { TabComponentProps } from "../utils/common.interface";

const { Title, Paragraph, Text } = Typography;

const API_BASE = process.env.REACT_APP_BACK_END_API || "";
const POLL_INTERVAL_MS = 3000;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Container = styled.div`
  padding: 48px 24px;
  max-width: 860px;
  margin: 0 auto;
`;

const IngestCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: none;
  .ant-card-body {
    padding: 40px;
  }
`;

const ProgressBox = styled.div`
  margin-top: 28px;
  padding: 24px;
  background: #f5f5f5;
  border-radius: 8px;
`;

const StatusLine = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 12px;
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IngestStatus = "idle" | "running" | "done" | "error";

interface JobData {
  status: string;
  metadata?: {
    percentComplete?: number;
    s3Key?: string;
    salesforceId?: string;
    salesforceError?: string;
    errorCode?: string;
    existingSalesforceId?: string;
  };
  message?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const REJECTED_PATTERNS = [
  { pattern: /dropbox\.com/i, label: "Dropbox" },
  { pattern: /drive\.google\.com/i, label: "Google Drive" },
  { pattern: /docs\.google\.com/i, label: "Google Drive" },
  { pattern: /^sftp:\/\//i, label: "SFTP" },
  { pattern: /^ftp:\/\//i, label: "FTP" },
  { pattern: /^s3:\/\//i, label: "private S3" },
];

function clientSideValidate(url: string): string | null {
  for (const { pattern, label } of REJECTED_PATTERNS) {
    if (pattern.test(url)) {
      return `${label} URLs are not supported. Paste the direct public HTTPS link.`;
    }
  }
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && u.protocol !== "http:") {
      return `Only HTTPS URLs are accepted.`;
    }
  } catch {
    return "Please enter a valid URL.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AmagiIngest: FC<TabComponentProps> = () => {
  const [url, setUrl] = useState("");
  const [fileNameOverride, setFileNameOverride] = useState("");
  const [length, setLength] = useState<number | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [ingestStatus, setIngestStatus] = useState<IngestStatus>("idle");
  const [job, setJob] = useState<JobData | null>(null);
  const [resolvedFileName, setResolvedFileName] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // -------------------------------------------------------------------------
  // Polling
  // -------------------------------------------------------------------------

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (operationId: string) => {
      pollRef.current = setInterval(async () => {
        try {
          const resp = await authAxios({ method: "GET", url: `${API_BASE}/api/jobs/${operationId}` });
          const jobData: JobData = resp.data;
          setJob(jobData);

          if (jobData.status === "COMPLETED" || jobData.status === "COMPLETED_WITH_ERRORS") {
            setIngestStatus("done");
            stopPolling();
          } else if (jobData.status === "FAILED" || jobData.status === "CANCELLED") {
            setIngestStatus("error");
            stopPolling();
          }
        } catch (err) {
          console.error("[AmagiIngest] Poll error:", err);
        }
      }, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleGo = useCallback(async () => {
    const trimmed = url.trim();
    const clientErr = clientSideValidate(trimmed);
    if (clientErr) {
      setValidationError(clientErr);
      return;
    }
    setValidationError(null);

    try {
      setIngestStatus("running");
      setJob(null);

      const resp = await authAxios({
        method: "POST",
        url: `${API_BASE}/amagi-ingest/start`,
        data: { url: trimmed, fileName: fileNameOverride.trim() || undefined, length },
      });

      const { operationId, fileName } = resp.data;
      setResolvedFileName(fileName);
      startPolling(operationId);
    } catch (err: any) {
      const msg =
        err.response?.data?.error || err.response?.data?.message || err.message || "Failed to start ingest.";
      setJob({ status: "FAILED", message: msg });
      setIngestStatus("error");
    }
  }, [url, fileNameOverride, startPolling]);

  const handleReset = useCallback(() => {
    stopPolling();
    setUrl("");
    setFileNameOverride("");
    setLength(undefined);
    setValidationError(null);
    setIngestStatus("idle");
    setJob(null);
    setResolvedFileName(null);
  }, [stopPolling]);

  // -------------------------------------------------------------------------
  // Derived display values
  // -------------------------------------------------------------------------

  const pct = job?.metadata?.percentComplete ?? (ingestStatus === "running" ? 5 : 0);
  const isRunning = ingestStatus === "running";
  const isDone = ingestStatus === "done";
  const isError = ingestStatus === "error";
  const showProgress = ingestStatus !== "idle";

  const progressStatus = isError ? "exception" : isDone ? "success" : "active";

  const statusIcon = isRunning ? (
    <LoadingOutlined spin style={{ color: "#1890ff", marginRight: 8 }} />
  ) : isDone ? (
    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
  ) : isError ? (
    <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
  ) : null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container>
      <Row justify="center">
        <Col xs={24}>
          <IngestCard>
            <Space direction="vertical" size="small" style={{ width: "100%", marginBottom: 24 }}>
              <Space align="center">
                <CloudUploadOutlined style={{ fontSize: 28, color: "#47cb38" }} />
                <Title level={3} style={{ margin: 0 }}>
                  Amagi S3 Ingest
                </Title>
              </Space>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Paste a public video URL. The file will be streamed directly into the Amagi ingest
                bucket and a Salesforce Video File record will be created automatically.
              </Paragraph>
            </Space>

            {/* URL input */}
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <div>
                <Text strong>Video URL</Text>
                <Tooltip title="Must be a plain public HTTPS link. Dropbox, Google Drive, SFTP, and private S3 URLs are not supported.">
                  <InfoCircleOutlined style={{ marginLeft: 6, color: "#999" }} />
                </Tooltip>
                <Input
                  placeholder="https://example.com/video.mp4"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setValidationError(null);
                  }}
                  onPressEnter={!isRunning ? handleGo : undefined}
                  disabled={isRunning}
                  size="large"
                  style={{ marginTop: 6 }}
                />
                {validationError && (
                  <Alert message={validationError} type="error" showIcon style={{ marginTop: 8 }} />
                )}
              </div>

              <div>
                <Text strong>File name </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (optional — leave blank to use the name from the URL)
                </Text>
                <Input
                  placeholder="my_video.mp4"
                  value={fileNameOverride}
                  onChange={(e) => setFileNameOverride(e.target.value)}
                  disabled={isRunning}
                  size="large"
                  style={{ marginTop: 6 }}
                />
              </div>

              <div>
                <Text strong>Length </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>(seconds)</Text>
                <Select
                  placeholder="Select length"
                  value={length}
                  onChange={setLength}
                  disabled={isRunning}
                  size="large"
                  style={{ marginTop: 6, width: "100%" }}
                  options={[15, 20, 30, 45, 60, 90, 120, 150, 180, 240, 300, 1710].map((s) => ({
                    value: s,
                    label: `${s}s`,
                  }))}
                />
              </div>

              <Row gutter={12}>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    icon={<CloudUploadOutlined />}
                    onClick={handleGo}
                    disabled={!url.trim() || isRunning}
                    loading={isRunning}
                  >
                    {isRunning ? "Ingesting…" : "Go"}
                  </Button>
                </Col>
                {(isDone || isError) && (
                  <Col>
                    <Button size="large" onClick={handleReset}>
                      Start over
                    </Button>
                  </Col>
                )}
              </Row>
            </Space>

            {/* Progress panel */}
            {showProgress && (
              <ProgressBox>
                <StatusLine>
                  {statusIcon}
                  {job?.message ?? "Starting…"}
                </StatusLine>

                <Progress
                  percent={pct}
                  status={progressStatus}
                  strokeColor={isDone ? "#52c41a" : "#1890ff"}
                />

                {resolvedFileName && (
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    File: <strong>{resolvedFileName}</strong>
                  </Text>
                )}

                {isDone && job?.metadata?.s3Key && (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Upload complete"
                    description={
                      <Space direction="vertical" size={2}>
                        <Text>
                          S3 key: <Text code>s3://amagicloud-nosey/{job.metadata.s3Key}</Text>
                        </Text>
                        {job.metadata.salesforceId && (
                          <Text>Salesforce record ID: <Text code>{job.metadata.salesforceId}</Text></Text>
                        )}
                        {job.metadata.salesforceError && (
                          <Text type="warning">
                            Salesforce: {job.metadata.salesforceError}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                )}

                {isError && job?.metadata?.errorCode === "DUPLICATE_FILE" && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Duplicate file — ingest rejected"
                    description={
                      <span>
                        A Salesforce Video File record for <strong>{resolvedFileName}</strong> already exists
                        {job.metadata?.existingSalesforceId && (
                          <> (ID: <code>{job.metadata.existingSalesforceId}</code>)</>
                        )}. An email notification has been sent to you. Contact the team if you need to replace the existing file.
                      </span>
                    }
                  />
                )}

                {isError && job?.metadata?.errorCode !== "DUPLICATE_FILE" && (
                  <Alert
                    message={job?.message || "Ingest failed"}
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                )}
              </ProgressBox>
            )}
          </IngestCard>
        </Col>
      </Row>
    </Container>
  );
};

export default AmagiIngest;
